import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  TextField,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toast } from 'react-hot-toast';
import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';
import NewDraftModal from './new_draft_modal';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

const { PRIVATE_ROUTE } = API_ROUTES;

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';

const fieldLabelSx = { fontSize: '0.78rem', fontWeight: 600, color: '#555', mb: 0.5 };

function PreviewRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.75 }}>
      <Typography sx={{ fontSize: '0.78rem', color: '#888', fontWeight: 600, minWidth: 110 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.82rem', color: '#222', fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

PreviewRow.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string };
PreviewRow.defaultProps = { value: '' };

export default function NewScheduleModal({ open, onClose, onSuccess }) {
  const { authAxios } = useContext(FetchContext);

  const [jobs, setJobs] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [costItems, setCostItems] = useState([]);

  const [formData, setFormData] = useState({ job: null, task: null, taskScope: '', costItem: '', estimatedHours: '', description: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'confirm'
  const [existingSchedule, setExistingSchedule] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const [openDraftConfirm, setOpenDraftConfirm] = useState(false);
  const [openNewDraftModal, setOpenNewDraftModal] = useState(false);
  const [draftInitialData, setDraftInitialData] = useState(null);

  useEffect(() => {
    if (!open) return;

    const fetchAll = async () => {
      try {
        const [tasksRes, costRes] = await Promise.all([
          authAxios.get(`${PRIVATE_ROUTE.GET_ACTIVE_TASKS_FOR_SCHEDULE}`),
          authAxios.get(`${PRIVATE_ROUTE.GET_LABOUR_COST_ITEMS}`),
        ]);

        const jobsData = tasksRes?.data?.data ?? [];
        setJobs(jobsData);
        setCostItems(costRes?.data?.data ?? []);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load scheduling data');
      }
    };

    fetchAll();
  }, [open, authAxios]);

  const handleJobChange = (job) => {
    setFormData({
      job,
      task: null,
      taskScope: '',
      costItem: formData.costItem,
      estimatedHours: formData.estimatedHours,
      description: '',
    });
    setFilteredTasks(job.task ?? []);
    if (errors.job) setErrors({ ...errors, job: undefined });
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: undefined });
  };

  const validate = () => {
    const e = {};
    if (!formData.job) e.job = 'Job is required';
    if (!formData.task) e.task = 'Task is required';
    if (!formData.taskScope) e.taskScope = 'Task scope is required';
    if (!formData.costItem) e.costItem = 'Cost item is required';
    if (!formData.estimatedHours && formData.estimatedHours !== 0) e.estimatedHours = 'Estimated hours is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsFetchingPreview(true);
    try {
      const params = new URLSearchParams({
        job_id: formData.job.job_number,
        task_id: formData.task._id,
        task_scope_id: formData.taskScope,
      });

      const scheduleRes = await authAxios.get(
        `${PRIVATE_ROUTE.FIND_SCHEDULE}?${params}`
      );

      setExistingSchedule(scheduleRes?.data?.statusCode === 404 ? null : (scheduleRes?.data?.data ?? null));
      setTaskDetails(formData.task);
      setStep('confirm');
    } catch {
      toast.error('Something went wrong while fetching schedule details.');
    } finally {
      setIsFetchingPreview(false);
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        task_id: formData.task._id,
        job_id: formData.job.job_number,
        task_scope_id: formData.taskScope,
        cost_item: formData.costItem,
        ...(formData.estimatedHours !== '' && { estimated_hours: parseFloat(formData.estimatedHours) }),
        ...(formData.description && { description: formData.description }),
      };
      const res = await authAxios.post('schedule/create', payload);
      if (res?.data?.statusCode === 200) {
        toast.success('Schedule created successfully');
        onSuccess();
        setDraftInitialData({
          job: formData.job,
          task: formData.task,
          taskScope: formData.taskScope,
          description: formData.description,
        });
        handleClose();
        setOpenDraftConfirm(true);
      } else {
        toast.error(res?.data?.message ?? 'Failed to create schedule');
        setStep('form');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
      setStep('form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewDraft = () => {
    setDraftInitialData({
      job: formData.job,
      task: formData.task,
      taskScope: formData.taskScope,
      description: formData.description,
    });

    setOpenNewDraftModal(true);
  };

  const handleClose = () => {
    setFormData({ job: null, task: null, taskScope: '', costItem: '', estimatedHours: '', description: '' });
    setErrors({});
    setFilteredTasks([]);
    setStep('form');
    setExistingSchedule(null);
    setTaskDetails(null);
    onClose();
  };

  const es = existingSchedule;
  const td = taskDetails;
  const previewRows = [
    { label: 'Task Number', value: es?.task?.number_str ?? formData.task?.number_str },
    { label: 'Job Number', value: es?.job_id?.number_str ?? formData.job?.job_number },

    {
      label: 'Client',
      value:
        es?.client_id?.company_name ??
        formData.job?.client_id?.company_name ??
        '—',
    },
    {
      label: 'Project Manager',
      value:
        (
          es?.project_managers ??
          formData.job?.project_managers ??
          []
        )
          .map(pm => `${pm.first_name} ${pm.last_name}`)
          .join(', ') || '—',
    },

    { label: 'City', value: es?.job_id?.locations?.[0]?.city ?? formData.job?.locations?.[0]?.city },
    {
      label: 'Task Scope',
      value:
        es?.task_scope_id?.name ??
        td?.task_scope_id?.name ??
        formData.task?.task_scope_name ??
        formData.taskScope,
    },

    { label: 'Cost Item', value: formData.costItem || (Array.isArray(es?.cost_item) ? es.cost_item.join(', ') : es?.cost_item) },
    { label: 'Estimated Hours', value: formData.estimatedHours !== '' ? String(formData.estimatedHours) : (es?.estimated_hours != null ? String(es.estimated_hours) : null) },
    { label: 'Description', value: formData.description },
  ];

  return (
    <>
      <Modal open={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 520 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CalendarMonthIcon sx={{ color: '#fff', fontSize: 22 }} />
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}>
                  SCHEDULING
                </Typography>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                  {step === 'confirm' ? 'Confirm Schedule' : 'New Schedule'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: '#fff' }} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {step === 'form' ? (
            /* ── Form ── */
            <Box sx={{ px: 3, py: 2.5 }}>
              <Grid container spacing={2}>
                {/* Select Job */}
                <Grid item xs={12}>
                  <Typography sx={{ ...fieldLabelSx, color: errors.job ? '#d32f2f' : '#555' }}>
                    Select Job <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Autocomplete
                    options={jobs}
                    getOptionLabel={(option) => option.job_number ?? ''}
                    value={formData.job}
                    onChange={(_, selected) => {
                      if (selected) handleJobChange(selected);
                      else {
                        setFormData({ job: null, task: null, taskScope: '', costItem: formData.costItem });
                        setFilteredTasks([]);
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search or select a job"
                        error={!!errors.job}
                        helperText={errors.job}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                      />
                    )}
                  />
                </Grid>

                {/* Task */}
                <Grid item xs={12}>
                  <Typography sx={{ ...fieldLabelSx, color: errors.task ? '#d32f2f' : '#555' }}>
                    Task <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <FormControl fullWidth size="small" error={!!errors.task}>
                    <Select
                      value={formData.task?._id ?? ''}
                      onChange={(e) => {
                        const selected = filteredTasks.find((t) => t._id === e.target.value);
                        if (selected) {
                          setFormData((prev) => ({
                            ...prev,
                            task: selected,
                            taskScope: selected.task_scope_name || selected.task_scope_id || '',
                            description: selected.description || '',
                          }));
                          if (errors.task) setErrors((prev) => ({ ...prev, task: undefined }));
                          if (errors.taskScope) setErrors((prev) => ({ ...prev, taskScope: undefined }));
                          if (formData.job && selected.task_scope_id) {
                            const p = new URLSearchParams({
                              job_id: formData.job.job_number,
                              task_id: selected._id,
                              task_scope_id: selected.task_scope_id,
                            });
                            authAxios.get(`${PRIVATE_ROUTE.FIND_SCHEDULE}?${p}`).then((res) => {
                              if (res?.data?.statusCode !== 404 && res?.data?.data?.description) {
                                setFormData((prev) => ({ ...prev, description: res.data.data.description }));
                              }
                            }).catch(() => { });
                          }
                        }
                      }}
                      displayEmpty
                      disabled={!formData.job}
                      sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                    >
                      <MenuItem value="" disabled>
                        {formData.job ? (filteredTasks.length ? 'Select a task' : 'No tasks for this job') : 'Select a job first'}
                      </MenuItem>
                      {filteredTasks.map((task) => (
                        <MenuItem key={task._id} value={task._id}>
                          {task.number_str}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.task && <FormHelperText>{errors.task}</FormHelperText>}
                  </FormControl>
                </Grid>

                {/* Task Scope */}
                <Grid item xs={12}>
                  <Typography sx={{ ...fieldLabelSx, color: errors.taskScope ? '#d32f2f' : '#555' }}>
                    Task Scope <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={formData.taskScope || ''}
                    disabled
                    error={!!errors.taskScope}
                    helperText={errors.taskScope}
                    placeholder="Auto-filled from task"
                    sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                  />
                </Grid>

                {/* Cost Item */}
                <Grid item xs={12}>
                  <Typography sx={{ ...fieldLabelSx, color: errors.costItem ? '#d32f2f' : '#555' }}>
                    Cost Item <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Autocomplete
                    options={costItems}
                    value={formData.costItem || null}
                    onChange={(_, value) => handleChange('costItem', value ?? '')}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search or select cost item"
                        error={!!errors.costItem}
                        helperText={errors.costItem}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                      />
                    )}
                  />
                </Grid>

                {/* Estimated Hours */}
                <Grid item xs={12}>
                  <Typography sx={{ ...fieldLabelSx, color: errors.estimatedHours ? '#d32f2f' : undefined }}>
                    Estimated Hours <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 0.5 }}
                    value={formData.estimatedHours}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || parseFloat(val) >= 0) handleChange('estimatedHours', val);
                    }}
                    placeholder="e.g. 4"
                    error={!!errors.estimatedHours}
                    helperText={errors.estimatedHours}
                    sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <Typography sx={fieldLabelSx}>
                    Description <span style={{ fontWeight: 400, color: '#999', fontSize: '0.72rem' }}>(optional)</span>
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Enter a description..."
                    sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL } }}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            /* ── Confirmation Preview ── */
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ color: TEAL_DARK, fontSize: 20 }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#555', fontWeight: 600 }}>
                  Please review the details before confirming.
                </Typography>
              </Box>
              <Box
                sx={{
                  border: `1px solid ${TEAL_LIGHT}`,
                  borderRadius: 1.5,
                  px: 2,
                  py: 0.5,
                  backgroundColor: '#f9fdfd',
                }}
              >
                {previewRows.map(({ label, value }, i) => (
                  <React.Fragment key={label}>
                    <PreviewRow label={label} value={value} />
                    {i < previewRows.length - 1 && <Divider sx={{ borderColor: TEAL_LIGHT }} />}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: `1px solid ${TEAL_LIGHT}`,
              backgroundColor: '#fafafa',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              {/* {step === 'confirm' ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => setStep('form')}
                  disabled={isSubmitting}
                  sx={{
                    borderColor: TEAL_DARK,
                    color: TEAL_DARK,
                    '&:hover': { borderColor: TEAL, backgroundColor: TEAL_LIGHT },
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  sx={{
                    backgroundColor: TEAL_DARK,
                    fontWeight: 700,
                    px: 3,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: TEAL, boxShadow: 'none' },
                  }}
                >
                  {isSubmitting ? 'Creating...' : 'Confirm'}
                </Button>
              </>
            ) */
                step === 'confirm' ? (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => setStep('form')}
                      disabled={isSubmitting}
                      sx={{
                        borderColor: TEAL_DARK,
                        color: TEAL_DARK,
                        '&:hover': {
                          borderColor: TEAL,
                          backgroundColor: TEAL_LIGHT,
                        },
                      }}
                    >
                      Back
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleConfirm}
                      disabled={isSubmitting}
                      sx={{
                        backgroundColor: TEAL_DARK,
                        fontWeight: 700,
                        px: 3,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: TEAL,
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {isSubmitting ? 'Creating...' : 'Confirm'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      sx={{
                        borderColor: TEAL_DARK,
                        color: TEAL_DARK,
                        '&:hover': { borderColor: TEAL, backgroundColor: TEAL_LIGHT },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isFetchingPreview}
                      sx={{
                        backgroundColor: TEAL_DARK,
                        fontWeight: 700,
                        px: 3,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: TEAL, boxShadow: 'none' },
                      }}
                    >
                      {isFetchingPreview ? 'Loading...' : 'Submit'}
                    </Button>
                  </>
                )}
            </Stack>
          </Box>
        </Box>
      </Modal>
      <Dialog
        open={openDraftConfirm}
        onClose={() => setOpenDraftConfirm(false)}
      >
        <DialogTitle>
          Create New Draft?
        </DialogTitle>

        <DialogContent>
          Do you want to create a New Draft for this schedule?
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenDraftConfirm(false);
            }}
          >
            No
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setOpenDraftConfirm(false);
              setOpenNewDraftModal(true);
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <NewDraftModal
        open={openNewDraftModal}
        onClose={() => setOpenNewDraftModal(false)}
        onSuccess={onSuccess}
        initialData={draftInitialData}
      />
    </>
  );
}

NewScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
